import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getWorkProductsByPhase } from "../../api/api";
import { WorkProduct } from "../../pages/interface/types";
import { mapApiToWorkProducts } from "../../pages/interface/utils/helpers";
import type { RootState } from "../store";

interface WorkProductState {
    workProductsByPhase: Record<string, WorkProduct[]>;
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
    activeRequestId: string | null;
}

const initialState: WorkProductState = {
    workProductsByPhase: {
        discovery: [],
        design: [],
        code: [],
        qa: [],
        deploy: []
    },
    status: "idle",
    error: null,
    activeRequestId: null
};

// Async thunk to fetch work products by phase
export const fetchWorkProductsByPhase = createAsyncThunk<
    Record<string, WorkProduct[]>,
    { projectId?: number; featureId?: number; userStoryId?: string; level?: "PRODUCT" | "FEATURE" | "USERSTORY" } | undefined,
    { rejectValue: string }
>("workproducts/fetchByPhase", async (params, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const token = state.clientToken.token;

    // Get project ID from parameter or from current project in state
    const currentProjectId = params?.projectId || state.projects.currentProject?.project_id;
    const featureId = params?.featureId;
    const userStoryId = params?.userStoryId;
    const level = params?.level;

    if (!token) {
        return rejectWithValue("No token available");
    }

    if (!currentProjectId) {
        return rejectWithValue("No project selected");
    }

    try {
        const response = await getWorkProductsByPhase(token, currentProjectId, featureId, userStoryId, level);
        // Map API response to WorkProduct interface
        const mappedWorkProducts = mapApiToWorkProducts(response);
        return mappedWorkProducts;
    } catch (error: any) {
        return rejectWithValue(error.message || "Failed to fetch work products");
    }
});

const workProductSlice = createSlice({
    name: "workproducts",
    initialState,
    reducers: {
        resetWorkProductState(state) {
            state.status = "idle";
            state.error = null;
            state.activeRequestId = null;
        },
        updateWorkProductState(state, action) {
            const { phase, workProducts } = action.payload;
            if (state.workProductsByPhase[phase]) {
                state.workProductsByPhase[phase] = workProducts;
            }
        }
    },
    extraReducers: builder => {
        builder
            // Handle fetchWorkProductsByPhase actions
            .addCase(fetchWorkProductsByPhase.pending, (state, action) => {
                state.status = "loading";
                state.error = null;
                state.activeRequestId = action.meta.requestId;
            })
            .addCase(fetchWorkProductsByPhase.fulfilled, (state, action) => {
                // Selection changes can issue Product, Feature, and User Story
                // requests close together. Ignore an older response so it
                // cannot replace the currently selected scope's card states.
                if (state.activeRequestId !== action.meta.requestId) return;
                state.status = "succeeded";
                state.activeRequestId = null;
                // Cast to any to avoid readonly/writable draft conflicts - Immer handles immutability
                state.workProductsByPhase = action.payload as any;
            })
            .addCase(fetchWorkProductsByPhase.rejected, (state, action) => {
                if (state.activeRequestId !== action.meta.requestId) return;
                state.status = "failed";
                state.error = action.payload as string;
                state.activeRequestId = null;
            });
    }
});

export const { resetWorkProductState, updateWorkProductState } = workProductSlice.actions;
export default workProductSlice.reducer;
